import { KsService, useApiClient } from '@siclop/kstone-vue'
import type { FunctionalityType } from '../models/FunctionalityType'

export class FunctionalityTypeService extends KsService {
  protected static mainUrl = '/functionality-types'

  /**
   * Get all active functionality types with their estimation scales
   */
  static async getActive(): Promise<FunctionalityType[]> {
    const client = useApiClient()
    return await client.getAll<FunctionalityType>(`${this.mainUrl}/active`)
  }

  /**
   * Get a functionality type with its estimation scales
   */
  static async getWithScales(id: number): Promise<FunctionalityType | null> {
    const client = useApiClient()
    return await client.get<FunctionalityType>(`${this.mainUrl}/${id}/with-scales`) ?? null
  }

  /**
   * Get functionality type by code
   */
  static async getByCode(code: string): Promise<FunctionalityType | null> {
    const client = useApiClient()
    return await client.get<FunctionalityType>(`${this.mainUrl}/code/${code}`) ?? null
  }

  /**
   * Toggle active status
   */
  static async toggleActive(id: number): Promise<FunctionalityType> {
    const client = useApiClient()
    const axios = client.getAxiosInstance()
    const response = await axios.patch<FunctionalityType>(`${this.mainUrl}/${id}/toggle-active`)
    return response.data
  }

  /**
   * Update estimation scales for a functionality type
   */
  static async updateScales(id: number, estimationScaleIds: number[]): Promise<FunctionalityType> {
    const client = useApiClient()
    const axios = client.getAxiosInstance()
    const response = await axios.put<FunctionalityType>(`${this.mainUrl}/${id}/scales`, estimationScaleIds)
    return response.data
  }

  /**
   * Save functionality type with its estimation scales in a single operation
   * Avoids multiple updates and reduces UI flickering
   */
  static async saveWithScales(model: Partial<FunctionalityType>): Promise<FunctionalityType> {
    const client = useApiClient()
    const axios = client.getAxiosInstance()
    const response = await axios.post<FunctionalityType>(`${this.mainUrl}/with-scales`, model)
    return response.data
  }
}
